import { clampPopoverLeft } from "./clampPopoverLeft";

/** Breathing room kept between an anchored tooltip and the viewport edge, in px. */
export const TOOLTIP_VIEWPORT_MARGIN = 8;

export interface TooltipAnchors {
	/** Vertical nudge from the trigger's top edge (negative lifts it above). */
	offsetY: number;
	/** Horizontal nudge from the trigger's left edge. */
	offsetX: number;
}

export const DEFAULT_TOOLTIP_ANCHORS: TooltipAnchors = { offsetY: -16, offsetX: 32 };

interface TriggerRect {
	top: number;
	bottom: number;
	left: number;
	right: number;
}

interface Size {
	width: number;
	height: number;
}

export interface ComputeTooltipPositionArgs {
	triggerRect: TriggerRect;
	tooltipSize: Size;
	viewport: Size;
	anchors?: TooltipAnchors;
	margin?: number;
}

export interface TooltipPosition {
	top: number;
	left: number;
	maxHeight?: number;
}

/**
 * Positions a `fixed` body-portalled tooltip inside the viewport.
 *
 * A fixed box cannot be scrolled into view, so a tooltip opened from a trigger low on the screen
 * used to hang past the bottom edge and take its Save button with it (PREC-4675). This keeps the
 * box where it has always been whenever it fits; near the bottom it flips above the trigger, and
 * when it fits neither side it is pinned to the top margin with a `maxHeight` so the whole box
 * scrolls inside itself. Horizontally it is clamped to the edges, an over-wide box pinned to the
 * left margin so its leading edge stays reachable (the same rule as `clampPopoverLeft`).
 *
 * The vertical flip mirrors the below placement around the trigger: `top + offsetY` is the anchor
 * edge on both sides, so the gap from the trigger's top stays the same.
 */
export const computeTooltipPosition = ({
	triggerRect,
	tooltipSize,
	viewport,
	anchors = DEFAULT_TOOLTIP_ANCHORS,
	margin = TOOLTIP_VIEWPORT_MARGIN,
}: ComputeTooltipPositionArgs): TooltipPosition => {
	const { offsetY, offsetX } = anchors;
	const bottomEdge = viewport.height - margin;

	let top = triggerRect.top + offsetY;
	let maxHeight: number | undefined;

	if (top + tooltipSize.height > bottomEdge) {
		const above = triggerRect.top + offsetY - tooltipSize.height;

		if (above >= margin) {
			top = above;
		} else {
			top = margin;
			maxHeight = viewport.height - margin * 2;
		}
	}

	const left = clampPopoverLeft(
		triggerRect.left + offsetX,
		tooltipSize.width,
		viewport.width,
		margin
	);

	return maxHeight === undefined ? { top, left } : { top, left, maxHeight };
};
